import {
  ArrowDown,
  ArrowUp,
  Circle,
  Copy,
  Download,
  LayoutGrid,
  Minus,
  MousePointer2,
  Pencil,
  Redo2,
  RotateCcw,
  RotateCw,
  Square,
  Trash2,
  Type,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type Ref } from 'react';
import { errorMessage } from '../lib/api';
import {
  boxOf,
  downloadFloorplan,
  emptyFloorplan,
  FILLS,
  FONT_SIZES,
  GRID,
  INKS,
  MAX_SHAPES,
  newShape,
  normalizeShape,
  PRESETS,
  SHAPE_NAME,
  SNAP,
  type Floorplan,
  type FloorplanShape,
  type ShapeKind,
} from '../lib/floorplan';
import { useToast } from './toast';
import { Button, cn, ConfirmDialog, Field, Input, Modal, Textarea } from './ui';

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

/** Text on one or more lines, centered on a point. */
function Words({ text, x, y, fontSize, fill, halo }: { text: string; x: number; y: number; fontSize: number; fill: string; halo?: boolean }) {
  const lines = text.split('\n');
  const lineHeight = fontSize * 1.2;
  return (
    <text
      x={x}
      y={y - ((lines.length - 1) * lineHeight) / 2}
      fontSize={fontSize}
      textAnchor="middle"
      dominantBaseline="central"
      fill={fill}
      {...(halo ? { stroke: '#ffffff', strokeWidth: fontSize / 4, paintOrder: 'stroke', strokeLinejoin: 'round' as const } : {})}
    >
      {lines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? 0 : lineHeight}>
          {line || ' '}
        </tspan>
      ))}
    </text>
  );
}

function ShapeView({ shape, markers }: { shape: FloorplanShape; markers: string }) {
  const box = boxOf(shape);
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  const dash = shape.dashed ? '10 6' : undefined;

  if (shape.kind === 'line') {
    const x2 = shape.x + shape.w;
    const y2 = shape.y + shape.h;
    return (
      <g data-shape-id={shape.id}>
        {/* A wider invisible line, so a thin line is easy to grab. */}
        <line x1={shape.x} y1={shape.y} x2={x2} y2={y2} stroke="transparent" strokeWidth={16} />
        <line
          x1={shape.x}
          y1={shape.y}
          x2={x2}
          y2={y2}
          stroke={shape.stroke}
          strokeWidth={3}
          strokeDasharray={dash}
          strokeLinecap="round"
          markerEnd={shape.arrow ? `url(#${markers}${shape.stroke.slice(1)})` : undefined}
        />
        {shape.label && <Words text={shape.label} x={(shape.x + x2) / 2} y={(shape.y + y2) / 2 - shape.fontSize} fontSize={shape.fontSize} fill={shape.stroke} halo />}
      </g>
    );
  }

  return (
    <g data-shape-id={shape.id} transform={shape.rotation ? `rotate(${shape.rotation} ${cx} ${cy})` : undefined}>
      {shape.kind === 'rect' && (
        <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={4} fill={shape.fill} stroke={shape.stroke} strokeWidth={2} strokeDasharray={dash} />
      )}
      {shape.kind === 'circle' && (
        <ellipse cx={cx} cy={cy} rx={box.w / 2} ry={box.h / 2} fill={shape.fill} stroke={shape.stroke} strokeWidth={2} strokeDasharray={dash} />
      )}
      {shape.kind === 'text' && <rect x={box.x} y={box.y} width={box.w} height={box.h} fill="transparent" />}
      {shape.label && <Words text={shape.label} x={cx} y={cy} fontSize={shape.fontSize} fill={shape.kind === 'text' ? shape.stroke : '#0f172a'} />}
    </g>
  );
}

/** A floorplan drawn as SVG. The editor draws its selection on top as children. */
export function FloorplanSvg({
  plan,
  svgRef,
  className,
  children,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  cursor,
}: {
  plan: Floorplan;
  svgRef?: Ref<SVGSVGElement>;
  className?: string;
  children?: ReactNode;
  onPointerDown?(event: ReactPointerEvent<SVGSVGElement>): void;
  onPointerMove?(event: ReactPointerEvent<SVGSVGElement>): void;
  onPointerUp?(event: ReactPointerEvent<SVGSVGElement>): void;
  cursor?: string;
}) {
  // Ids unique to this drawing, since a page can show more than one.
  const prefix = `fp${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const arrowColors = [...new Set(plan.shapes.filter((shape) => shape.kind === 'line' && shape.arrow).map((shape) => shape.stroke))];

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${plan.width} ${plan.height}`}
      className={className}
      fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
      style={{ touchAction: 'none', cursor }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <defs>
        <pattern id={`${prefix}grid`} width={GRID} height={GRID} patternUnits="userSpaceOnUse">
          <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="#e2e8f0" strokeWidth={1} />
        </pattern>
        {arrowColors.map((color) => (
          <marker key={color} id={`${prefix}arrow${color.slice(1)}`} viewBox="0 0 10 10" refX={8} refY={5} markerWidth={5} markerHeight={5} orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
          </marker>
        ))}
      </defs>
      <rect x={0} y={0} width={plan.width} height={plan.height} fill="#ffffff" />
      <rect x={0} y={0} width={plan.width} height={plan.height} fill={`url(#${prefix}grid)`} pointerEvents="none" />
      {plan.shapes.map((shape) => (
        <ShapeView key={shape.id} shape={shape} markers={`${prefix}arrow`} />
      ))}
      {children}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Editing
// ---------------------------------------------------------------------------

type Tool = 'select' | ShapeKind;
type Point = { x: number; y: number };
type Handle = 'nw' | 'ne' | 'sw' | 'se' | 'start' | 'end';

interface Drag {
  mode: 'create' | 'move' | 'resize';
  id: string;
  handle?: Handle;
  start: Point;
  original: FloorplanShape;
  /** The drawing before the drag, kept for undo once something actually moves. */
  before: FloorplanShape[];
  moved: boolean;
}

const TOOLS: { tool: Tool; label: string; key: string; icon: ReactNode }[] = [
  { tool: 'select', label: 'Select and move', key: 'V', icon: <MousePointer2 /> },
  { tool: 'rect', label: 'Rectangle', key: 'R', icon: <Square /> },
  { tool: 'circle', label: 'Circle', key: 'C', icon: <Circle /> },
  { tool: 'line', label: 'Line or arrow', key: 'L', icon: <Minus /> },
  { tool: 'text', label: 'Text', key: 'T', icon: <Type /> },
];

/** Keeps a drag going when the pointer leaves the drawing. Some pointers can't be captured; the drag still works inside. */
function capture(event: ReactPointerEvent<SVGSVGElement>) {
  try {
    event.currentTarget.setPointerCapture(event.pointerId);
  } catch {
    // Nothing to do: moves over the drawing still arrive.
  }
}

const rotate = (x: number, y: number, degrees: number) => {
  const radians = (degrees * Math.PI) / 180;
  return { x: x * Math.cos(radians) - y * Math.sin(radians), y: x * Math.sin(radians) + y * Math.cos(radians) };
};

/** A line's run, turned to the nearest 45 degrees. */
function straighten(dx: number, dy: number) {
  const length = Math.hypot(dx, dy);
  const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
  return { dx: Math.round(length * Math.cos(angle)), dy: Math.round(length * Math.sin(angle)) };
}

/**
 * A box resized from one corner, in the shape's own turned frame, keeping the opposite corner where it is on screen.
 */
function resizeBox(original: FloorplanShape, handle: Handle, pointer: Point, square: boolean, snap: (value: number) => number) {
  const box = boxOf(original);
  const center = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
  const turned = rotate(pointer.x - center.x, pointer.y - center.y, -original.rotation);
  const local = original.rotation ? { x: turned.x + center.x, y: turned.y + center.y } : { x: snap(pointer.x), y: snap(pointer.y) };
  const fixed = { x: handle.includes('w') ? box.x + box.w : box.x, y: handle.includes('n') ? box.y + box.h : box.y };
  let w = Math.max(10, Math.abs(local.x - fixed.x));
  let h = Math.max(10, Math.abs(local.y - fixed.y));
  if (square) w = h = Math.max(w, h);
  const x = handle.includes('w') ? fixed.x - w : fixed.x;
  const y = handle.includes('n') ? fixed.y - h : fixed.y;
  const moved = { x: x + w / 2, y: y + h / 2 };
  const before = rotate(fixed.x - center.x, fixed.y - center.y, original.rotation);
  const after = rotate(fixed.x - moved.x, fixed.y - moved.y, original.rotation);
  return { x: x + before.x + center.x - (after.x + moved.x), y: y + before.y + center.y - (after.y + moved.y), w, h };
}

function Swatches({ colors, value, onChange }: { colors: { value: string; label: string }[]; value: string; onChange(value: string): void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {colors.map((color) => (
        <button
          key={color.value}
          type="button"
          title={color.label}
          aria-label={color.label}
          aria-pressed={value === color.value}
          onClick={() => onChange(color.value)}
          className={cn(
            'size-6 rounded-full border border-slate-300',
            value === color.value && 'ring-2 ring-brand-500 ring-offset-1',
            color.value === 'none' && 'bg-[linear-gradient(135deg,transparent_45%,#ef4444_45%,#ef4444_55%,transparent_55%)]',
          )}
          style={color.value === 'none' ? undefined : { backgroundColor: color.value }}
        />
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, children }: { checked: boolean; onChange(value: boolean): void; children: ReactNode }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="size-4 rounded border-slate-300" />
      {children}
    </label>
  );
}

const numberFrom = (value: string, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** The floorplan editor, in a popup: shapes, labels, colors, and sizes on a grid. */
export function FloorplanEditor({
  open,
  initial,
  title = 'Draw floorplan',
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: Floorplan | null;
  title?: string;
  saving?: boolean;
  onClose(): void;
  onSave(plan: Floorplan): void;
}) {
  const toast = useToast();
  const svgRef = useRef<SVGSVGElement>(null);
  const exportRef = useRef<SVGSVGElement>(null);
  const labelRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const base = useMemo(() => initial ?? emptyFloorplan(), [initial]);

  const [shapes, setShapes] = useState<FloorplanShape[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>('select');
  const [snapOn, setSnapOn] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [discarding, setDiscarding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [historySize, setHistorySize] = useState({ past: 0, future: 0 });
  const [screenScale, setScreenScale] = useState(1);
  const past = useRef<FloorplanShape[][]>([]);
  const future = useRef<FloorplanShape[][]>([]);
  const lastEdit = useRef<{ key: string; at: number } | null>(null);
  const drag = useRef<Drag | null>(null);
  const initialJson = useRef('[]');

  // A fresh drawing each time it opens.
  useEffect(() => {
    if (!open) return;
    const start = base.shapes.map((shape) => ({ ...shape }));
    setShapes(start);
    initialJson.current = JSON.stringify(start);
    setSelectedId(null);
    setTool('select');
    setZoom(1);
    past.current = [];
    future.current = [];
    lastEdit.current = null;
    setHistorySize({ past: 0, future: 0 });
  }, [open, base]);

  // How many screen pixels a drawing unit is, so handles stay the same size on screen.
  useEffect(() => {
    const svg = svgRef.current;
    if (!open || !svg) return;
    const measure = () => setScreenScale(svg.getBoundingClientRect().width / base.width || 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [open, base.width, zoom]);

  const plan: Floorplan = { version: 1, width: base.width, height: base.height, shapes };
  const selected = shapes.find((shape) => shape.id === selectedId) ?? null;
  const dirty = open && JSON.stringify(shapes) !== initialJson.current;
  const snap = (value: number) => (snapOn ? Math.round(value / SNAP) * SNAP : Math.round(value));

  const syncHistory = () => setHistorySize({ past: past.current.length, future: future.current.length });
  const remember = (snapshot: FloorplanShape[]) => {
    past.current = [...past.current.slice(-99), snapshot];
    future.current = [];
    syncHistory();
  };

  /** Changes the drawing. Edits to the same thing in quick succession (typing a label) undo together. */
  const change = (next: FloorplanShape[], key?: string) => {
    const now = Date.now();
    if (!key || lastEdit.current?.key !== key || now - lastEdit.current.at > 800) remember(shapes);
    lastEdit.current = key ? { key, at: now } : null;
    setShapes(next);
  };

  const update = (id: string, patch: Partial<FloorplanShape>, key = `${id}:${Object.keys(patch).join(',')}`) =>
    change(
      shapes.map((shape) => (shape.id === id ? { ...shape, ...patch } : shape)),
      key,
    );

  const add = (shape: FloorplanShape) => {
    if (shapes.length >= MAX_SHAPES) {
      toast.error(`A floorplan can have up to ${MAX_SHAPES} shapes`);
      return;
    }
    change([...shapes, shape]);
    setSelectedId(shape.id);
    setTool('select');
  };

  const undo = () => {
    const previous = past.current.pop();
    if (!previous) return;
    future.current.push(shapes);
    lastEdit.current = null;
    setShapes(previous);
    if (selectedId && !previous.some((shape) => shape.id === selectedId)) setSelectedId(null);
    syncHistory();
  };

  const redo = () => {
    const next = future.current.pop();
    if (!next) return;
    past.current.push(shapes);
    lastEdit.current = null;
    setShapes(next);
    if (selectedId && !next.some((shape) => shape.id === selectedId)) setSelectedId(null);
    syncHistory();
  };

  const remove = (id: string) => {
    change(shapes.filter((shape) => shape.id !== id));
    setSelectedId(null);
  };

  const duplicate = (shape: FloorplanShape) => add({ ...shape, id: newShape(shape.kind, 0, 0).id, x: shape.x + 20, y: shape.y + 20 });

  const reorder = (id: string, toFront: boolean) => {
    const shape = shapes.find((row) => row.id === id);
    if (!shape) return;
    const rest = shapes.filter((row) => row.id !== id);
    change(toFront ? [...rest, shape] : [shape, ...rest]);
  };

  /** Scales a shape around its center. */
  const scaleShape = (shape: FloorplanShape, factor: number) => {
    if (shape.kind === 'text') return update(shape.id, { fontSize: Math.min(120, Math.max(8, Math.round(shape.fontSize * factor))) }, `${shape.id}:scale`);
    if (shape.kind === 'line') return update(shape.id, { w: Math.round(shape.w * factor), h: Math.round(shape.h * factor) }, `${shape.id}:scale`);
    const box = boxOf(shape);
    const w = Math.max(10, Math.round(box.w * factor));
    const h = Math.max(10, Math.round(box.h * factor));
    update(shape.id, { x: Math.round(box.x + (box.w - w) / 2), y: Math.round(box.y + (box.h - h) / 2), w, h }, `${shape.id}:scale`);
  };

  const addPreset = (preset: (typeof PRESETS)[number]) => {
    const { kind, ...rest } = preset.shape;
    const w = rest.w ?? 0;
    const h = rest.h ?? 0;
    // In the middle of the drawing, nudged so repeated pieces don't stack exactly.
    const offset = (shapes.length % 8) * 20;
    const x = snap(base.width / 2 - (kind === 'line' ? 0 : w / 2) + offset);
    const y = snap(base.height / 2 - (kind === 'line' ? h / 2 : h / 2) + offset);
    add(newShape(kind, x, y, rest));
  };

  const requestClose = () => (dirty ? setDiscarding(true) : onClose());

  const save = () => {
    setSelectedId(null);
    onSave({ version: 1, width: base.width, height: base.height, shapes: shapes.map(normalizeShape) });
  };

  const download = async () => {
    if (!exportRef.current) return;
    try {
      await downloadFloorplan(exportRef.current, plan, title);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // ---- Pointer ------------------------------------------------------------------------------------------------------
  const toPoint = (event: ReactPointerEvent<SVGSVGElement>): Point => {
    const svg = svgRef.current!;
    const matrix = svg.getScreenCTM();
    if (!matrix) return { x: 0, y: 0 };
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
    return { x: point.x, y: point.y };
  };

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return;
    const target = event.target as Element;
    const point = toPoint(event);
    const handle = target.closest('[data-handle]')?.getAttribute('data-handle') as Handle | null;
    const begin = (next: Omit<Drag, 'start' | 'before' | 'moved'>) => {
      drag.current = { ...next, start: point, before: shapes, moved: false };
      capture(event);
    };

    if (handle && selected) {
      begin({ mode: 'resize', id: selected.id, handle, original: selected });
      return;
    }
    if (tool === 'text') {
      const shape = newShape('text', snap(point.x), snap(point.y));
      add(shape);
      setTimeout(() => {
        labelRef.current?.focus();
        labelRef.current?.select();
      }, 0);
      return;
    }
    if (tool !== 'select') {
      if (shapes.length >= MAX_SHAPES) {
        toast.error(`A floorplan can have up to ${MAX_SHAPES} shapes`);
        return;
      }
      const shape = newShape(tool, snap(point.x), snap(point.y), { w: 0, h: 0 });
      setShapes([...shapes, shape]);
      setSelectedId(shape.id);
      drag.current = { mode: 'create', id: shape.id, start: point, original: shape, before: shapes, moved: true };
      capture(event);
      return;
    }
    const id = target.closest('[data-shape-id]')?.getAttribute('data-shape-id');
    const shape = id ? shapes.find((row) => row.id === id) : null;
    if (shape) {
      setSelectedId(shape.id);
      begin({ mode: 'move', id: shape.id, original: shape });
      return;
    }
    setSelectedId(null);
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const current = drag.current;
    if (!current) return;
    const point = toPoint(event);
    const dx = point.x - current.start.x;
    const dy = point.y - current.start.y;
    if (!current.moved) {
      if (Math.hypot(dx, dy) < 2) return;
      current.moved = true;
      remember(current.before);
      lastEdit.current = null;
    }
    const { original } = current;
    let patch: Partial<FloorplanShape>;

    if (current.mode === 'move') {
      patch = { x: snap(original.x + dx), y: snap(original.y + dy) };
    } else if (current.mode === 'create') {
      let w = snap(point.x) - original.x;
      let h = snap(point.y) - original.y;
      if (original.kind === 'line') {
        if (event.shiftKey) ({ dx: w, dy: h } = straighten(w, h));
      } else if (original.kind === 'circle' || event.shiftKey) {
        const size = Math.max(Math.abs(w), Math.abs(h));
        w = Math.sign(w || 1) * size;
        h = Math.sign(h || 1) * size;
      }
      patch = { w, h };
    } else if (original.kind === 'line') {
      const endX = original.x + original.w;
      const endY = original.y + original.h;
      if (current.handle === 'start') {
        let run = { dx: endX - snap(point.x), dy: endY - snap(point.y) };
        if (event.shiftKey) run = straighten(run.dx, run.dy);
        patch = { x: endX - run.dx, y: endY - run.dy, w: run.dx, h: run.dy };
      } else {
        let run = { dx: snap(point.x) - original.x, dy: snap(point.y) - original.y };
        if (event.shiftKey) run = straighten(run.dx, run.dy);
        patch = { w: run.dx, h: run.dy };
      }
    } else if (original.kind === 'text') {
      const box = boxOf(original);
      const local = rotate(dx, dy, -original.rotation);
      const factor = Math.max(0.2, (box.h + local.y) / box.h);
      patch = { fontSize: Math.min(120, Math.max(8, Math.round(original.fontSize * factor))) };
    } else {
      patch = resizeBox(original, current.handle!, point, original.kind === 'circle' || event.shiftKey, snap);
    }
    setShapes((rows) => rows.map((shape) => (shape.id === current.id ? { ...shape, ...patch } : shape)));
  };

  const onPointerUp = (event: ReactPointerEvent<SVGSVGElement>) => {
    const current = drag.current;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (current?.mode !== 'create') return;

    remember(current.before);
    lastEdit.current = null;
    setShapes((rows) =>
      rows.map((shape) => {
        if (shape.id !== current.id) return shape;
        const tiny = Math.abs(shape.w) < 6 && Math.abs(shape.h) < 6;
        if (!tiny) return shape.kind === 'line' ? shape : normalizeShape(shape);
        // A click without dragging drops a shape of the usual size there.
        const sized = newShape(shape.kind, 0, 0);
        return shape.kind === 'line'
          ? { ...shape, w: sized.w, h: sized.h }
          : { ...shape, x: snap(current.start.x - sized.w / 2), y: snap(current.start.y - sized.h / 2), w: sized.w, h: sized.h };
      }),
    );
    setTool('select');
  };

  // ---- Keyboard -----------------------------------------------------------------------------------------------------
  const keys = useRef<(event: KeyboardEvent) => void>(() => undefined);
  keys.current = (event: KeyboardEvent) => {
    if (discarding || clearing) return;
    const target = event.target instanceof HTMLElement ? event.target : null;
    const typing = target?.closest('input, textarea, select, [contenteditable="true"]');
    const mod = event.ctrlKey || event.metaKey;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (typing) target?.blur();
      else if (tool !== 'select') setTool('select');
      else if (selectedId) setSelectedId(null);
      else requestClose();
      return;
    }
    if (typing) return;
    if (mod && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    } else if (mod && event.key.toLowerCase() === 'y') {
      event.preventDefault();
      redo();
    } else if (mod && event.key.toLowerCase() === 'd' && selected) {
      event.preventDefault();
      duplicate(selected);
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && selected) {
      event.preventDefault();
      remove(selected.id);
    } else if (event.key.startsWith('Arrow') && selected) {
      event.preventDefault();
      const step = event.shiftKey ? GRID : snapOn ? SNAP : 1;
      const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
      const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
      update(selected.id, { x: selected.x + dx, y: selected.y + dy }, `${selected.id}:nudge`);
    } else if (!mod && !event.altKey) {
      const match = TOOLS.find((row) => row.key === event.key.toUpperCase());
      if (match) setTool(match.tool);
    }
  };

  useEffect(() => {
    if (!open) return;
    const listener = (event: KeyboardEvent) => keys.current(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [open]);

  // ---- Selection ----------------------------------------------------------------------------------------------------
  const handleSize = 10 / screenScale;
  const handleProps = (handle: Handle, x: number, y: number) => ({
    'data-handle': handle,
    x: x - handleSize / 2,
    y: y - handleSize / 2,
    width: handleSize,
    height: handleSize,
    rx: handleSize / 5,
    fill: '#ffffff',
    stroke: '#0284c7',
    strokeWidth: 1.5 / screenScale,
    style: { cursor: handle === 'nw' || handle === 'se' ? 'nwse-resize' : handle === 'ne' || handle === 'sw' ? 'nesw-resize' : 'move' },
  });

  let overlay: ReactNode = null;
  if (selected) {
    const stroke = { stroke: '#0284c7', strokeWidth: 1.5 / screenScale, strokeDasharray: `${6 / screenScale} ${4 / screenScale}`, fill: 'none', pointerEvents: 'none' as const };
    if (selected.kind === 'line') {
      overlay = (
        <g>
          <rect {...handleProps('start', selected.x, selected.y)} />
          <rect {...handleProps('end', selected.x + selected.w, selected.y + selected.h)} />
        </g>
      );
    } else {
      const box = boxOf(selected);
      const pad = 4 / screenScale;
      const cx = box.x + box.w / 2;
      const cy = box.y + box.h / 2;
      overlay = (
        <g transform={selected.rotation ? `rotate(${selected.rotation} ${cx} ${cy})` : undefined}>
          <rect x={box.x - pad} y={box.y - pad} width={box.w + pad * 2} height={box.h + pad * 2} {...stroke} />
          {selected.kind === 'text' ? (
            <rect {...handleProps('se', box.x + box.w + pad, box.y + box.h + pad)} />
          ) : (
            (['nw', 'ne', 'sw', 'se'] as const).map((handle) => (
              <rect key={handle} {...handleProps(handle, handle.includes('w') ? box.x : box.x + box.w, handle.includes('n') ? box.y : box.y + box.h)} />
            ))
          )}
        </g>
      );
    }
  }

  const lineLength = selected?.kind === 'line' ? Math.round(Math.hypot(selected.w, selected.h)) : 0;

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        dismissible={false}
        size="2xl"
        title={title}
        description="Add shapes and labels, drag to move them, and pull the corners to resize. Shift keeps things square or straight."
        footer={
          <>
            <Button variant="ghost" icon={<Download />} onClick={download} className="mr-auto">
              Download image
            </Button>
            <Button onClick={requestClose}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={save}>
              Save floorplan
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {TOOLS.map((row) => (
                <button
                  key={row.tool}
                  type="button"
                  title={`${row.label} (${row.key})`}
                  aria-label={row.label}
                  aria-pressed={tool === row.tool}
                  onClick={() => setTool(row.tool)}
                  className={cn(
                    'rounded-md p-2 text-slate-600 [&_svg]:size-4',
                    tool === row.tool ? 'bg-white text-brand-800 shadow-sm ring-1 ring-slate-200' : 'hover:bg-white hover:text-slate-900',
                  )}
                >
                  {row.icon}
                </button>
              ))}
              <span className="mx-1 h-6 w-px bg-slate-200" />
              <button type="button" title="Undo (Ctrl+Z)" aria-label="Undo" disabled={historySize.past === 0} onClick={undo} className="rounded-md p-2 text-slate-600 hover:bg-white disabled:opacity-40 [&_svg]:size-4">
                <Undo2 />
              </button>
              <button type="button" title="Redo (Ctrl+Shift+Z)" aria-label="Redo" disabled={historySize.future === 0} onClick={redo} className="rounded-md p-2 text-slate-600 hover:bg-white disabled:opacity-40 [&_svg]:size-4">
                <Redo2 />
              </button>
              <span className="mx-1 h-6 w-px bg-slate-200" />
              <button
                type="button"
                title="Snap to the grid"
                aria-pressed={snapOn}
                onClick={() => setSnapOn((value) => !value)}
                className={cn('flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium [&_svg]:size-4', snapOn ? 'bg-white text-brand-800 shadow-sm ring-1 ring-slate-200' : 'text-slate-600 hover:bg-white')}
              >
                <LayoutGrid /> Snap
              </button>
              <button type="button" title="Zoom out" aria-label="Zoom out" disabled={zoom <= 1} onClick={() => setZoom((value) => Math.max(1, value - 0.5))} className="rounded-md p-2 text-slate-600 hover:bg-white disabled:opacity-40 [&_svg]:size-4">
                <ZoomOut />
              </button>
              <span className="w-10 text-center text-xs text-slate-500">{Math.round(zoom * 100)}%</span>
              <button type="button" title="Zoom in" aria-label="Zoom in" disabled={zoom >= 3} onClick={() => setZoom((value) => Math.min(3, value + 0.5))} className="rounded-md p-2 text-slate-600 hover:bg-white disabled:opacity-40 [&_svg]:size-4">
                <ZoomIn />
              </button>
              <span className="ml-auto pr-1 text-xs text-slate-500">
                {shapes.length} shape{shapes.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-slate-500">Quick add</span>
              {PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => addPreset(preset)}
                  className="rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-brand-300 hover:bg-brand-50/60"
                >
                  {preset.name}
                </button>
              ))}
            </div>

            <div className="max-h-[62vh] overflow-auto rounded-lg border border-slate-200">
              <div style={{ width: `${zoom * 100}%` }}>
                <FloorplanSvg
                  plan={plan}
                  svgRef={svgRef}
                  className="block h-auto w-full select-none"
                  cursor={tool === 'select' ? 'default' : 'crosshair'}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                >
                  {overlay}
                </FloorplanSvg>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              {tool === 'select'
                ? 'Click a shape to select it. Delete removes it, the arrow keys nudge it, and Ctrl+D copies it.'
                : tool === 'text'
                  ? 'Click where the text goes, then type it on the right.'
                  : `Drag to draw a ${SHAPE_NAME[tool].toLowerCase()}, or click to drop one of the usual size.`}
            </p>
          </div>

          <aside className="w-full shrink-0 space-y-4 lg:w-64">
            {selected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">{SHAPE_NAME[selected.kind]}</p>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" icon={<Copy />} onClick={() => duplicate(selected)} aria-label="Copy" title="Copy (Ctrl+D)" />
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => remove(selected.id)} aria-label="Delete" title="Delete" />
                  </div>
                </div>

                <Field label={selected.kind === 'text' ? 'Text' : 'Label'}>
                  {selected.kind === 'text' ? (
                    <Textarea
                      ref={labelRef}
                      rows={2}
                      maxLength={200}
                      value={selected.label}
                      onChange={(event) => update(selected.id, { label: event.target.value })}
                    />
                  ) : (
                    <Input
                      ref={labelRef}
                      maxLength={200}
                      value={selected.label}
                      placeholder={selected.kind === 'line' ? 'e.g. Entrance' : 'e.g. Stage'}
                      onChange={(event) => update(selected.id, { label: event.target.value })}
                    />
                  )}
                </Field>

                <Field as="div" label={selected.kind === 'text' ? 'Text size' : 'Label size'}>
                  <div className="flex flex-wrap items-center gap-1">
                    {FONT_SIZES.map((size) => (
                      <button
                        key={size.value}
                        type="button"
                        onClick={() => update(selected.id, { fontSize: size.value })}
                        className={cn(
                          'min-w-9 rounded-md border px-2 py-1 text-xs font-medium',
                          selected.fontSize === size.value ? 'border-brand-400 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                        )}
                      >
                        {size.label}
                      </button>
                    ))}
                    <Input
                      type="number"
                      min={8}
                      max={120}
                      value={selected.fontSize}
                      aria-label="Font size"
                      onChange={(event) => update(selected.id, { fontSize: Math.min(120, Math.max(8, numberFrom(event.target.value, selected.fontSize))) })}
                      className="w-16"
                    />
                  </div>
                </Field>

                <Field as="div" label="Size">
                  {selected.kind === 'rect' && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={10}
                        aria-label="Width"
                        value={Math.round(Math.abs(selected.w))}
                        onChange={(event) => update(selected.id, { w: Math.max(10, numberFrom(event.target.value, selected.w)) })}
                      />
                      <span className="text-slate-400">×</span>
                      <Input
                        type="number"
                        min={10}
                        aria-label="Height"
                        value={Math.round(Math.abs(selected.h))}
                        onChange={(event) => update(selected.id, { h: Math.max(10, numberFrom(event.target.value, selected.h)) })}
                      />
                    </div>
                  )}
                  {selected.kind === 'circle' && (
                    <Input
                      type="number"
                      min={10}
                      aria-label="Diameter"
                      value={Math.round(Math.abs(selected.w))}
                      onChange={(event) => {
                        const box = boxOf(selected);
                        const size = Math.max(10, numberFrom(event.target.value, box.w));
                        update(selected.id, { x: box.x + (box.w - size) / 2, y: box.y + (box.h - size) / 2, w: size, h: size });
                      }}
                    />
                  )}
                  {selected.kind === 'line' && (
                    <Input
                      type="number"
                      min={10}
                      aria-label="Length"
                      value={lineLength}
                      onChange={(event) => {
                        const length = Math.max(10, numberFrom(event.target.value, lineLength));
                        const angle = Math.atan2(selected.h, selected.w);
                        update(selected.id, { w: Math.round(length * Math.cos(angle)), h: Math.round(length * Math.sin(angle)) });
                      }}
                    />
                  )}
                  <div className="mt-1.5 flex gap-1">
                    <Button size="sm" onClick={() => scaleShape(selected, 0.8)}>
                      Smaller
                    </Button>
                    <Button size="sm" onClick={() => scaleShape(selected, 1.25)}>
                      Larger
                    </Button>
                  </div>
                </Field>

                {(selected.kind === 'rect' || selected.kind === 'circle') && (
                  <Field as="div" label="Fill">
                    <Swatches colors={FILLS} value={selected.fill} onChange={(fill) => update(selected.id, { fill })} />
                  </Field>
                )}
                <Field as="div" label={selected.kind === 'text' ? 'Color' : selected.kind === 'line' ? 'Line' : 'Outline'}>
                  <Swatches colors={INKS} value={selected.stroke} onChange={(stroke) => update(selected.id, { stroke })} />
                </Field>

                {selected.kind !== 'text' && (
                  <div className="space-y-1.5">
                    <Toggle checked={selected.dashed} onChange={(dashed) => update(selected.id, { dashed })}>
                      Dashed
                    </Toggle>
                    {selected.kind === 'line' && (
                      <Toggle checked={selected.arrow} onChange={(arrow) => update(selected.id, { arrow })}>
                        Arrowhead
                      </Toggle>
                    )}
                  </div>
                )}

                {selected.kind !== 'line' && (
                  <Field as="div" label="Turn">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" icon={<RotateCcw />} aria-label="Turn left" onClick={() => update(selected.id, { rotation: (selected.rotation + 345) % 360 }, `${selected.id}:rotation`)} />
                      <Input
                        type="number"
                        min={0}
                        max={359}
                        aria-label="Degrees"
                        value={selected.rotation}
                        onChange={(event) => update(selected.id, { rotation: ((Math.round(numberFrom(event.target.value, selected.rotation)) % 360) + 360) % 360 })}
                        className="w-20"
                      />
                      <Button size="sm" variant="ghost" icon={<RotateCw />} aria-label="Turn right" onClick={() => update(selected.id, { rotation: (selected.rotation + 15) % 360 }, `${selected.id}:rotation`)} />
                    </div>
                  </Field>
                )}

                <div className="flex gap-1">
                  <Button size="sm" icon={<ArrowUp />} onClick={() => reorder(selected.id, true)}>
                    To front
                  </Button>
                  <Button size="sm" icon={<ArrowDown />} onClick={() => reorder(selected.id, false)}>
                    To back
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-900">Nothing selected</p>
                <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                  <p className="font-medium text-slate-800">Tips</p>
                  <p>Pick a tool above, or press R, C, L, or T.</p>
                  <p>Select a shape to label it, color it, resize it, or turn it.</p>
                  <p>Ctrl+Z undoes; Esc steps back.</p>
                </div>
                {shapes.length > 0 && (
                  <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setClearing(true)}>
                    Clear the drawing
                  </Button>
                )}
              </div>
            )}
          </aside>
        </div>

        {/* A clean copy without the selection, for the image download. */}
        <div className="hidden">
          <FloorplanSvg plan={plan} svgRef={exportRef} />
        </div>
      </Modal>

      <ConfirmDialog
        open={discarding}
        onClose={() => setDiscarding(false)}
        onConfirm={() => {
          setDiscarding(false);
          onClose();
        }}
        title="Discard your changes?"
        description="The floorplan goes back to how it was when you opened it."
        confirmLabel="Discard"
      />
      <ConfirmDialog
        open={clearing}
        onClose={() => setClearing(false)}
        onConfirm={() => {
          change([]);
          setSelectedId(null);
          setClearing(false);
        }}
        title="Clear the drawing?"
        description="Every shape is removed. Undo brings them back."
        confirmLabel="Clear"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// On a page
// ---------------------------------------------------------------------------

/**
 * A floorplan on a page: a preview that opens full size, a download, and (for people who can change it) Draw, Edit, and
 * Remove. `onChange` saves; when it throws, the editor stays open.
 */
export function FloorplanPanel({
  plan,
  canEdit,
  saving,
  onChange,
  name,
  meta,
  emptyText = 'No floorplan yet.',
}: {
  plan: Floorplan | null;
  canEdit: boolean;
  saving?: boolean;
  onChange?(plan: Floorplan | null): unknown;
  /** Used for the image's file name. */
  name: string;
  meta?: ReactNode;
  emptyText?: string;
}) {
  const toast = useToast();
  const previewRef = useRef<SVGSVGElement>(null);
  const [editing, setEditing] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const download = async () => {
    if (!plan || !previewRef.current) return;
    try {
      await downloadFloorplan(previewRef.current, plan, `${name} floorplan`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const save = async (next: Floorplan | null) => {
    try {
      await onChange?.(next);
      return true;
    } catch {
      // Whoever saves shows what went wrong.
      return false;
    }
  };

  return (
    <div className="space-y-3">
      {plan ? (
        <>
          <button
            type="button"
            onClick={() => setViewing(true)}
            className="block w-full overflow-hidden rounded-lg border border-slate-200 transition-colors hover:border-brand-300"
            aria-label="Open the floorplan full size"
          >
            <FloorplanSvg plan={plan} svgRef={previewRef} className="block h-auto w-full" />
          </button>
          {meta && <p className="text-xs text-slate-500">{meta}</p>}
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button size="sm" icon={<Pencil />} onClick={() => setEditing(true)}>
                Edit floorplan
              </Button>
            )}
            <Button size="sm" variant="ghost" icon={<Download />} onClick={download}>
              Download
            </Button>
            {canEdit && (
              <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setRemoving(true)}>
                Remove
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-slate-300 px-4 py-6 text-center">
          <p className="text-sm text-slate-600">{emptyText}</p>
          {canEdit && (
            <Button className="mt-3" icon={<Pencil />} onClick={() => setEditing(true)}>
              Draw floorplan
            </Button>
          )}
        </div>
      )}

      <FloorplanEditor
        open={editing}
        initial={plan}
        title={`${name ? `${name}: ` : ''}floorplan`}
        saving={saving}
        onClose={() => setEditing(false)}
        onSave={async (next) => {
          if (await save(next)) setEditing(false);
        }}
      />

      {plan && (
        <Modal
          open={viewing}
          onClose={() => setViewing(false)}
          size="xl"
          title="Floorplan"
          description={name}
          footer={
            <>
              <Button icon={<Download />} onClick={download}>
                Download image
              </Button>
              <Button variant="primary" onClick={() => setViewing(false)}>
                Close
              </Button>
            </>
          }
        >
          <FloorplanSvg plan={plan} className="block h-auto w-full rounded-lg border border-slate-200" />
        </Modal>
      )}

      <ConfirmDialog
        open={removing}
        onClose={() => setRemoving(false)}
        onConfirm={async () => {
          if (await save(null)) setRemoving(false);
        }}
        loading={saving}
        title="Remove the floorplan?"
        description="The drawing is deleted from this event request."
        confirmLabel="Remove"
      />
    </div>
  );
}
